'use client'

import airbridge from 'airbridge-web-sdk-loader'
import {
  EventOption,
  WaitCallback,
} from 'airbridge-web-sdk-loader/build/airbridge_api'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { OpenDeeplinkOption } from './context'

const initializeAirbridge = ({
  onInitialized,
}: {
  onInitialized: () => void
}) => {
  if (airbridge.isSDKEnabled) {
    onInitialized()

    return
  }

  const app = process.env.NEXT_PUBLIC_AIRBRIDGE_APP_NAME
  const webToken = process.env.NEXT_PUBLIC_AIRBRIDGE_WEB_TOKEN

  if (!app || !webToken) {
    return
  }

  airbridge.init(
    {
      app,
      webToken,
      useProtectedAttributionWindow: true,
      protectedAttributionWindowInMinutes: 4320,
      utmParsing: true,
    },
    error => {
      if (!error) {
        onInitialized()
      }
    },
  )
}

export const useAirbridge = () => {
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined' || isInitialized) {
      return
    }

    initializeAirbridge({
      onInitialized: () => {
        setIsInitialized(true)
      },
    })
  }, [])

  const setUser = useCallback((userId: string) => {
    if (!airbridge.isSDKEnabled) {
      return
    }

    airbridge.setUserID(userId)
  }, [])

  const clearUser = useCallback(() => {
    if (!airbridge.isSDKEnabled) {
      return
    }

    airbridge.clearUser()
  }, [])

  const sendEvent = useCallback(
    ({
      event,
      params,
      onSuccess,
    }: {
      event: string
      params: EventOption
      onSuccess?: WaitCallback
    }) => {
      if (!airbridge.isSDKEnabled) {
        return
      }

      airbridge.events.send(event, params)

      if (onSuccess) {
        airbridge.events.wait(3000, onSuccess)
      }
    },
    [],
  )

  const openDeeplink = useCallback(
    (openDeeplinkOption?: OpenDeeplinkOption) => {
      if (!airbridge.isSDKEnabled) {
        return
      }

      const DEFAULT_DEEPLINK_OPTIONS: OpenDeeplinkOption = {
        type: 'click',
        deeplinks: {
          ios: 'spacev33m2://app?type=gh',
          android: 'spacev33m2://app?type=gh',
          desktop: `${window.location.origin}/guest/main`,
        },
        fallbacks: {
          ios: 'itunes-appstore',
          android: 'google-play',
        },
      }

      airbridge.openDeeplink({
        ...DEFAULT_DEEPLINK_OPTIONS,
        ...openDeeplinkOption,
      })
    },
    [],
  )

  const values = useMemo(
    () => ({ isInitialized, setUser, clearUser, sendEvent, openDeeplink }),
    [isInitialized, setUser, clearUser, sendEvent, openDeeplink],
  )

  return values
}
